
import React, { useEffect, useState } from "react";
import SubscriptionManager from "@/components/SubscriptionManager";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Download, AlertTriangle } from "lucide-react";

const BinWashSubscriptions = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [supabaseStatus, setSupabaseStatus] = useState<string>("Checking...");
  const { user, loading, isAdmin } = useAuth();

  useEffect(() => {
    console.log("BinWashSubscriptions component mounted");
    console.log("Current user email:", user?.email);
    console.log("Is admin check result:", isAdmin);
    
    // Check if Supabase client exists and log
    if (window.supabaseClient) {
      console.log("Supabase client found in BinWashSubscriptions");
      setSupabaseStatus("Client available");
      
      // Only run connection test for admin users
      if (isAdmin) {
        console.log("Running Supabase connection test for admin user");
        // Test a simple query to verify connection
        const checkConnection = async () => {
          try {
            const response = await supabase
              .from('subscriptions')
              .select('count');
            
            console.log("Supabase connection test result:", response);
            if (response.error) {
              setSupabaseStatus(`Error: ${response.error.message}`);
            } else {
              setSupabaseStatus(`Connected successfully`);
            }
          } catch (err) {
            console.error("Supabase connection test failed:", err);
            setSupabaseStatus(`Connection error: ${err instanceof Error ? err.message : String(err)}`);
          }
        };
        
        checkConnection();
      }
    } else {
      console.warn("No Supabase client detected in BinWashSubscriptions");
      setSupabaseStatus("Not connected");
    }
    
    try {
      setIsLoaded(true);
    } catch (err) {
      console.error("Error in BinWashSubscriptions:", err);
      setError(err instanceof Error ? err.message : "Unknown error");
    }
  }, [isAdmin, user]);

  // If the authentication is still loading, show a loading message
  if (loading) {
    return <div className="container mx-auto py-8 px-4 text-center">Loading authentication status...</div>;
  }

  // If not logged in, redirect to auth page
  if (!user) {
    return <Navigate to="/auth" />;
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <h1 className="text-3xl font-bold mb-6">Bin Wash Subscriptions</h1>
      
      {/* Admin banner - only show for admin users */}
      {isAdmin && (
        <div className="mb-6 bg-red-100 p-4 rounded-lg border-2 border-red-500 text-red-800">
          <h2 className="text-xl font-bold mb-2">ADMIN MODE ACTIVE</h2>
          <p>You are logged in as an administrator ({user?.email}). You can view all subscriptions and access the admin controls below.</p>
          <p className="mt-2 font-semibold">The admin export button is shown below.</p>
          <p className="mt-2">If you cannot see the admin controls, please refresh the page or log out and log back in.</p>
        </div>
      )}
      
      {/* Debug information - only show for admins */}
      {isAdmin && (
        <div className="mb-4 bg-gray-50 p-3 rounded border">
          <p className="text-sm text-gray-500">Debug: BinWashSubscriptions component, isLoaded = {String(isLoaded)}</p>
          <p className="text-sm text-gray-500">Debug: Supabase status: {supabaseStatus}</p>
          <p className="text-sm text-gray-500">Debug: User email: {user?.email}</p>
          <p className="text-sm text-gray-500">Debug: Is admin: {String(isAdmin)}</p>
          {error && <p className="text-sm text-red-500">Error: {error}</p>}
        </div>
      )}
      
      {error && isAdmin ? (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded mb-4">
          <p>Error loading page: {error}</p>
        </div>
      ) : !isLoaded ? (
        <div className="p-4 text-center">Loading page content...</div>
      ) : (
        <div data-testid="subscription-container" className="bg-white p-6 rounded-lg shadow-md">
          <SubscriptionManager showHeading={false} />
        </div>
      )}
    </div>
  );
}

export default BinWashSubscriptions;
